import React, { useEffect, useMemo, useState } from 'react';
import { Send, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, EmptyState, PlatformIcon, StatusPill } from './mccUi';
import { PLATFORMS, humanDate, platformMeta } from './mccConstants';
import MccQueueHelp from './MccQueueHelp';

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const monthLabel = (d) => d.toLocaleString('en-AU', { month: 'long', year: 'numeric' });

export default function MccPublishing({ onOpenProject, onNav }) {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const from = startOfMonth(cursor).toISOString().slice(0, 10);
      const to = endOfMonth(cursor).toISOString().slice(0, 10);
      const res = await mccGet('/marketing/publishing/schedule', {
        from_date: from, to_date: to, platform: platform === 'all' ? '' : platform,
      });
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load publishing schedule'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor.getTime(), platform]);

  const grid = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const startDow = start.getDay();
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= end.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      const t = (it.scheduled_at || it.published_at || '').slice(0, 10);
      if (!t) continue;
      (map[t] = map[t] || []).push(it);
    }
    return map;
  }, [items]);

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Publishing Schedule"
        subtitle="Auto-populates from approved projects with a scheduled date. Prevents publishing anything unapproved."
        icon={Send}
        right={
          <div className="flex items-center gap-2">
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All platforms</SelectItem>
                {PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))}><ChevronLeft className="w-3 h-3" /></Button>
            <p className="text-sm font-bold text-slate-900 w-36 text-center">{monthLabel(cursor)}</p>
            <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))}><ChevronRight className="w-3 h-3" /></Button>
          </div>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[110px] bg-slate-50 border-b border-r border-slate-100" />;
            const key = d.toISOString().slice(0, 10);
            const list = byDay[key] || [];
            const isToday = key === new Date().toISOString().slice(0, 10);
            return (
              <div key={i} className="min-h-[110px] border-b border-r border-slate-100 p-1.5 relative">
                <p className={`text-[11px] font-bold ${isToday ? 'text-white bg-indigo-600 w-5 h-5 rounded-full inline-flex items-center justify-center' : 'text-slate-500'}`}>{d.getDate()}</p>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 3).map((it, idx) => {
                    const meta = platformMeta(it.platform);
                    const isPub = !!it.published_at;
                    const tone = isPub ? '#065f46' : '#4f46e5';
                    return (
                      <button key={idx} onClick={() => onOpenProject(it.project_id)}
                        className="w-full text-left rounded px-1.5 py-1 text-[10px] flex items-center gap-1 border-l-2"
                        style={{ background: `${meta?.tone || '#4f46e5'}22`, borderLeftColor: tone }}
                      >
                        <PlatformIcon platform={it.platform} size={14} />
                        <span className="truncate flex-1">{it.title || it.project_title}</span>
                      </button>
                    );
                  })}
                  {list.length > 3 && <p className="text-[10px] text-slate-500">+{list.length - 3} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-slate-800 mb-2">{loading ? 'Loading…' : `${items.length} scheduled / published in ${monthLabel(cursor)}`}</p>
        {items.length === 0 ? (
          <MccQueueHelp
            title="How the Publishing Schedule fills up"
            steps={[
              { n: 1, label: 'Approve a version', hint: 'From Approvals, mark the version "Approved & Ready to Publish".' },
              { n: 2, label: 'Add publishing details', hint: 'Open the project → Publishing tab → set date, caption and CTA per platform.' },
              { n: 3, label: 'It lands on the calendar', hint: 'Every scheduled or published item shows up here on its date.' },
              { n: 4, label: 'Mark as published', hint: 'Once posted, click "Mark as published" and paste the live link.' },
            ]}
            ctaLabel="Go to Approvals"
            onCta={() => onNav && onNav('approvals')}
            secondaryLabel="Open Filming Calendar"
            onSecondary={() => onNav && onNav('filming-calendar')}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it, i) => (
              <div key={`${it.project_id}-${it.platform}-${i}`}
                className="border border-slate-200 rounded-xl bg-white p-3 hover:shadow-md">
                <div className="flex items-center gap-2 mb-1">
                  <PlatformIcon platform={it.platform} size={22} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{platformMeta(it.platform)?.l}</p>
                    <p className="text-sm font-bold text-slate-900 truncate">{it.title || it.project_title}</p>
                  </div>
                  <StatusPill status={it.published_at ? 'published' : 'scheduled_to_publish'} size="sm" />
                </div>
                <p className="text-[11px] text-slate-500">
                  {it.published_at ? `Published ${humanDate(it.published_at)}` : `Scheduled ${humanDate(it.scheduled_at)}`}
                </p>
                {it.caption ? <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{it.caption}</p> : null}
                <div className="flex justify-between mt-2">
                  <button onClick={() => onOpenProject(it.project_id)} className="text-[11px] text-indigo-600 hover:underline">Open project</button>
                  {it.published_link && (
                    <a href={it.published_link} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1">
                      Open link <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
